import * as React from "react";
import type {
    HasOverlayState,
    HasOverlayProps,
} from "../../../ui/smart/has-overlay-component/has-overlay-component";
import HasOverlayComponent from "../../../ui/smart/has-overlay-component/has-overlay-component";
import type { Subtract } from "utility-types";
import type { AppEnvironment } from "common/components/types";
import type { AuthScreenMode } from "./app-modal-provider-types";
import AuthController from "../auth-controller/auth-controller";
import { ThemeConsumer } from "styled-components";
import { AppAuthenticationKind } from "common/components/SerializedApp";
import { styled } from "../../../styled-components";
import { getLastKnownAppLogin } from "webapp/glide-app-player";

export interface ModalController {
    readonly openAuthModal: (title: string, mode: AuthScreenMode, forceEdit?: boolean) => Promise<boolean>;
}

const AppModalsContext = React.createContext<ModalController>({
    openAuthModal: async (_title: string, _mode: AuthScreenMode) => false,
});

interface Props extends HasOverlayProps {
    env: AppEnvironment;
}
interface State extends HasOverlayState {
    modal: "share" | "auth" | undefined;
    onClose?: (val: any) => void;
    authModalInfo?: {
        title: string;
        mode: AuthScreenMode;
        forceEdit?: boolean;
    };
}

const WrapperHack = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
`;

export class AppModalProvider extends HasOverlayComponent<Props, State> {
    public state: State = {
        modal: undefined,
    };

    private onClose = (result: any): void => {
        this.hideOverlay();
        if (this.state.onClose !== undefined) {
            this.state.onClose(result);
        }
    };

    private openAuthModal = async (title: string, mode: AuthScreenMode, forceEdit?: boolean): Promise<boolean> => {
        const result = new Promise<boolean>(r => {
            this.setState({ onClose: r });
        });
        this.setState({
            modal: "auth",
            authModalInfo: {
                title,
                mode,
                forceEdit,
            },
        });
        this.showOverlay();

        return result;
    };

    private value: ModalController = {
        openAuthModal: this.openAuthModal,
    };

    public render(): React.ReactNode {
        const { env, renderEnvironment } = this.props;

        let portal: React.ReactNode;
        if (this.shouldRenderPortal()) {
            if (this.state.modal === "share") {
                portal = this.getPortal(<div />);
            } else if (this.state.modal === "auth" && this.state.authModalInfo !== undefined) {
                // const info = this.state.authModalInfo;

                const className = renderEnvironment.sizeClass === "tablet" ? "modal-anim-tag-tablet" : "modal-anim-tag";

                const login = getLastKnownAppLogin();

                portal = this.getPortal(
                    <ThemeConsumer>
                        {theme => (
                            <WrapperHack className={className}>
                                <AuthController
                                    previewMode={false}
                                    getUsername={true}
                                    onLoggedIn={async () => this.onClose(true)}
                                    onClose={() => this.onClose(false)}
                                    withBranding={false}
                                    appID={env.appID}
                                    appFeatures={env.appFeatures}
                                    accentColor={login?.theme.primaryAccentColor ?? theme.primaryAccentColor}
                                    authKind={AppAuthenticationKind.EmailPin}
                                />
                            </WrapperHack>
                        )}
                    </ThemeConsumer>
                );
            }
        }
        return (
            <AppModalsContext.Provider value={this.value}>
                {this.props.children}
                {portal}
            </AppModalsContext.Provider>
        );
    }
}

interface WithModalsProps {
    modals: ModalController;
}
export const withModals = <P extends WithModalsProps>(Component: React.ComponentType<P>) =>
    class WithAppID extends React.Component<Subtract<P, WithModalsProps>> {
        public render() {
            return (
                <AppModalsContext.Consumer>
                    {modals => <Component {...(this.props as P)} modals={modals} />}
                </AppModalsContext.Consumer>
            );
        }
    };
